/** The line grid: its columns, its mode, the internal band and the margin cell (moved from `builder/rows.tsx` at step 8). */
import { createContext, useContext, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'

/**
 * The columns a line row is — one grid for the quote and the package.
 *
 * Aaron, looking at the two screens side by side: "they need to look exactly
 * the same." So the gap, the indent, the height and the type are one value,
 * and the one thing that differs is the quantity cell: 80 wide for a number
 * somebody typed, 206 for a package's rule ("0.3 + 0.00005 / sq ft", with
 * what it comes to at the sample beneath it). Board 5, `build-both-together`
 * §3 row 1.
 */
const LINE_COLUMNS = 'items-center gap-2.5'
/**
 * The quote's line grid, by how wide the card is (folder 44 §5.1, §5.5).
 *
 * Left to right it reads as a sum — Qty · Unit · Price each · Price — and then
 * ours, on a tint behind a rule: Cost · Margin. The band is **one** grid cell
 * holding its own two columns, so the tint is one element the height of the
 * row rather than two cells that each paint half of it.
 *
 * Narrower than 860 the card loses Price each (it folds under the name as
 * "× $55.11 each") and Margin (one tap away on the visit's chip). The header
 * and every row read the same `mode` from {@link LineGridScope}, so they
 * cannot disagree about which columns exist.
 */
export type LineMode = 'all' | 'compact'

/**
 * What the quantity cell is (`BuilderAdapter.qty`): a number somebody typed,
 * or — on a package — a rule the measure resolves (§3 row 1, Board 5).
 */
export type QtyMode = 'number' | 'rule'

/** Below this card width, Price each folds and Margin leaves (§5.5). */
export const LINE_COMPACT_BELOW = 860

/**
 * How much wider a rule's quantity cell is than a number's: 206 against 80
 * (`build-both-together` B5). Every width threshold moves by it, so a package
 * card folds its columns at the same room for the name as a quote card does.
 */
export const RULE_QTY_EXTRA = 126

/*
 * Spelled out whole rather than built from the widths, because Tailwind reads
 * source text: a class assembled at run time is a class it never generated.
 */
const LINE_GRIDS: Record<QtyMode, Record<LineMode, string>> = {
  number: {
    all: `grid grid-cols-[24px_minmax(0,1fr)_80px_52px_88px_96px_max-content_28px] ${LINE_COLUMNS}`,
    compact: `grid grid-cols-[24px_minmax(0,1fr)_80px_52px_96px_max-content_28px] ${LINE_COLUMNS}`,
  },
  rule: {
    all: `grid grid-cols-[24px_minmax(0,1fr)_206px_52px_88px_96px_max-content_28px] ${LINE_COLUMNS}`,
    compact: `grid grid-cols-[24px_minmax(0,1fr)_206px_52px_96px_max-content_28px] ${LINE_COLUMNS}`,
  },
}

export const lineGrid = (mode: LineMode, qty: QtyMode = 'number'): string => LINE_GRIDS[qty][mode]

interface LineGridContext { mode: LineMode; qty: QtyMode; targetPct: number }
const LineGrid = createContext<LineGridContext>({ mode: 'all', qty: 'number', targetPct: 0 })

/** The mode and margin target the rows inside a {@link LineGridScope} draw with. */
export const useLineGrid = (): LineGridContext => useContext(LineGrid)

/**
 * Measures the card it sits in and tells every row which columns it has.
 *
 * A `ResizeObserver`, not a media query: the builder's rail changes the
 * card's width without changing the viewport (§5.5).
 */
export function LineGridScope({ targetPct, qty = 'number', children }: {
  targetPct: number
  /** The adapter's quantity cell — a rule widens it and moves the threshold. */
  qty?: QtyMode
  children: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [mode, setMode] = useState<LineMode>('all')
  const below = LINE_COMPACT_BELOW + (qty === 'rule' ? RULE_QTY_EXTRA : 0)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const measure = () => {
      const width = node.getBoundingClientRect().width
      // Nothing laid out yet (a test DOM, a hidden tab): keep every column.
      if (width === 0) return
      setMode(width >= below ? 'all' : 'compact')
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [below])

  return (
    <div ref={ref} data-line-mode={mode} data-qty-mode={qty}>
      <LineGrid.Provider value={{ mode, qty, targetPct }}>{children}</LineGrid.Provider>
    </div>
  )
}

/**
 * Ours: the cost and the margin, on the internal tint behind a rule (§5.4).
 *
 * `align-self: stretch` with the row's own vertical padding cancelled, so the
 * tint runs the full height of the row whatever the row's padding is.
 */
export function InternalBand({ cost, margin, inset, alt }: {
  cost: ReactNode
  margin?: ReactNode
  /** The row's vertical padding, cancelled so the band is full height. */
  inset: '-my-[7px]' | '-my-2.5' | 'my-0'
  /** The tinted rows (a service row) take the darker of the two tints. */
  alt?: boolean
}) {
  const { mode } = useLineGrid()
  return (
    <div
      data-internal-band
      className={cn('grid items-center gap-x-2.5 self-stretch border-l border-line-soft px-2.5',
        inset, alt ? 'bg-surface-internal-alt' : 'bg-surface-internal',
        mode === 'all' ? 'grid-cols-[88px_56px]' : 'grid-cols-[88px]')}
    >
      {cost}
      {mode === 'all' && (margin ?? <span />)}
    </div>
  )
}

/**
 * A line's margin: muted, or amber with a dot under the company's target —
 * words and colour both (§2 rule 6 is the panel's; the dot is the cell's).
 */
export function MarginCell({ pct, emphasis }: { pct: number | null; emphasis?: boolean }) {
  const { targetPct } = useLineGrid()
  if (pct === null) return <span className="text-right text-[12.5px] text-text-faint">—</span>
  const under = pct < targetPct
  return (
    <span
      className={cn('tnum whitespace-nowrap text-right text-[12.5px]',
        under ? 'font-semibold text-warn' : cn('text-text-muted', emphasis ? 'font-semibold' : 'font-medium'))}
    >
      {under && <i aria-hidden className="mr-[5px] inline-block h-1.5 w-1.5 rounded-full bg-warn" />}
      {pct.toFixed(1)}%
    </span>
  )
}
